import { useMemo, useState } from "react";

import { IHandBookTemplateExpenditure } from "pages/HandBook/model/types";
import { getResourceSymbol } from "pages/HandBook/utils/resourceNames";
import { findSegmentsById } from "pages/HandBook/utils/segments";

import { ICommonColumn } from "../templates/HandBooksTemplates";
import { useTypedSelector } from "app/store/typedUseSelector";

import { LOCALIZATION_CONFIG } from "constants/localization";

import { transformDigitToFinancial } from "utils/formatters/transformDigitToFinancial";

import styles from "./HandBooksTemplateExpenditureRow.module.scss";

interface IExtendedColumn<T extends Record<string, any>> extends ICommonColumn<T> {
  size: {
    base: number;
    editing: number;
  };
  hidden?: boolean;
}

interface IuseHandbookTemplatesExpendituresColumns {
  isEditing: boolean;
}

const HANDBOOK_TEMPLATES_EXPENDITURE_COLUMNS_LOCALSTORAGE_KEY = "tk.h.e";

export const useHandbookTemplatesExpendituresColumns = ({ isEditing }: IuseHandbookTemplatesExpendituresColumns) => {
  const allSegments = useTypedSelector((state) => state.handBookReducer.allSegments);
  const isAttributesView = useTypedSelector((state) => state.handBookReducer.templateViewFilters.attribute);
  const [hiddenCols, setHiddenCols] = useState<Record<string, boolean>>(
    JSON.parse(localStorage.getItem(HANDBOOK_TEMPLATES_EXPENDITURE_COLUMNS_LOCALSTORAGE_KEY) || "{}")
  );

  const columns: IExtendedColumn<IHandBookTemplateExpenditure>[] = useMemo(
    () =>
      (
        [
          isAttributesView && {
            header: { title: "Раздел", filterName: "parent_section_name" },
            render: (item) => {
              return <span>{item.parent_section_name}</span>;
            },
            size: {
              base: 7,
              editing: 7,
            },
            hidden: hiddenCols["Раздел"],
          },
          isAttributesView && {
            header: { title: "Подраздел", filterName: "section_name" },
            render: (item) => {
              return <span>{item.section_name}</span>;
            },
            size: {
              base: 9,
              editing: 9,
            },
            hidden: hiddenCols["Подраздел"],
          },
          {
            header: { title: "Наименование", filterName: "name" },
            render: (item) => (
              <span className={item.manual_expenditure.type !== "work" ? styles.materialNameCol : styles.workNameCol}>
                {item.manual_expenditure.type !== "work" ? (
                  <span className={styles.materialSymbol}>{getResourceSymbol(item.manual_expenditure.type)}</span>
                ) : null}
                {item.manual_expenditure?.name}
              </span>
            ),
            size: {
              base: 46,
              editing:
                49 -
                (isAttributesView && (!hiddenCols["Раздел"]) ? 7 : 0) -
                (isAttributesView && (!hiddenCols["Подраздел"]) ? 9 : 0),
            },
            hidden: hiddenCols["Наименование"],
          },
          (isEditing || !isAttributesView) && {
            header: { title: "Атрибут 1" },
            render: (item) => {
              if (!item.segment) return null;
              const { parentSegment } = findSegmentsById(allSegments, item.segment);
              return <span>{parentSegment?.name}</span>;
            },
            size: {
              base: 10,
              editing: 11,
            },
            hidden: hiddenCols["Атрибут 1"],
          },
          (isEditing || !isAttributesView) && {
            header: { title: "Атрибут 2" },
            render: (item) => {
              if (!item.segment) return null;
              const { segment } = findSegmentsById(allSegments, item.segment);
              return <span>{segment?.name}</span>;
            },
            size: {
              base: 10,
              editing: 11,
            },
            hidden: hiddenCols["Атрибут 2"],
          },
          {
            header: { title: "Ед. изм." },
            render: (item) => item.manual_expenditure?.measure,
            isCentered: true,
            size: {
              base: 7,
              editing: 7,
            },
            hidden: hiddenCols["Ед. изм."],
          },
          {
            header: { title: "Кол-во" },
            render: (item) => Number(item.count ?? item.manual_expenditure?.count) || 0,
            isCentered: true,
            size: {
              base: 7,
              editing: 7,
            },
            hidden: hiddenCols["Кол-во"],
          },
          {
            header: { title: `Цена ${LOCALIZATION_CONFIG.currency}` },
            render: (item) => transformDigitToFinancial(Number(item.price ?? item.manual_expenditure?.price) || 0),
            isRighted: true,
            size: {
              base: 7,
              editing: 7,
            },
            hidden: hiddenCols[`Цена ${LOCALIZATION_CONFIG.currency}`],
          },
          !isEditing && {
            header: { title: `Стоимость ${LOCALIZATION_CONFIG.currency}` },
            render: (item) =>
              transformDigitToFinancial(
                (Number(item.price ?? item.manual_expenditure?.price) || 0) *
                  (Number(item.count ?? item.manual_expenditure?.count) || 0)
              ),
            isRighted: true,
            size: {
              base: 10,
              editing: 10,
            },
            hidden: hiddenCols[`Стоимость ${LOCALIZATION_CONFIG.currency}`],
          },
        ] as IExtendedColumn<IHandBookTemplateExpenditure>[]
      ).filter((x) => x),
    [isEditing, allSegments, hiddenCols]
  );

  const toggleColHidden = (colTitle: string) => {
    const storeCandidate = { ...hiddenCols, [colTitle]: !Boolean(hiddenCols[colTitle]) };
    setHiddenCols(storeCandidate);
    localStorage.setItem(
      HANDBOOK_TEMPLATES_EXPENDITURE_COLUMNS_LOCALSTORAGE_KEY,
      JSON.stringify(Object.fromEntries(Object.entries(storeCandidate).filter(([_, v]) => v)))
    );
  };

  const displayColumns = useMemo(
    () =>
      columns.filter((c) => {
        if (isEditing) {
          if (c.header.title === "Раздел" || c.header.title === "Подраздел") {
            return !c.hidden;
          }
          return true;
        }
        return !c.hidden;
      }),
    [columns, isEditing]
  );

  const colPercents = useMemo(
    () => (isEditing ? [3, ...displayColumns.map((c) => c.size.editing)] : displayColumns.map((c) => c.size.base)),
    [isEditing, displayColumns]
  );
  const formColPercents = [3, 73, 7, 7, 7];

  const columnsForTableSettings = useMemo(() => (isEditing ? columns.slice(0, 2) : columns), [isEditing, columns]);

  return {
    columns,
    colPercents,
    formColPercents,
    displayColumns,
    toggleColHidden,
    hiddenCols,
    columnsForTableSettings,
  };
};
